import { useEffect, useRef } from 'react';
import { SCALE, type Mineral, type RunState } from '@wikigold/engine';
import { t } from '../lib';
import { CAMERA, CLAW_ANCHORS, CLAW_SIZE, hookPose, type HookMotionState } from './hook-motion';
import { currentAssetVersion, isSpriteDecoded, sprite } from './sprite-assets';
import spriteMetrics from './sprite-metrics.json';

export type CanvasGameState = HookMotionState & {
  dateKey: string;
  tick: number;
  phase: string;
  shots: number;
  objects: Mineral[];
  aimActive?: boolean;
};

function drawImage(ctx: CanvasRenderingContext2D, name: string, x: number, y: number, width: number, height = width) {
  const image = sprite(name);
  if (isSpriteDecoded(image) && image.naturalWidth > 0) ctx.drawImage(image, x, y, width, height);
}
const minerals = { small: 'gold_small', medium: 'gold_medium', large: 'gold_large', stone: 'rock', box: 'mystery_box', brand: 'brand_ore' };
function drawMineral(ctx: CanvasRenderingContext2D, object: Mineral, day: string, x = object.x, y = object.y) {
  const radius = object.radius / SCALE;
  const name = (object.kind === 'treasure' ? `treasure_${day}` : minerals[object.kind]) as keyof typeof spriteMetrics;
  const metrics = spriteMetrics[name], scale = radius / metrics.radius;
  // The old arbitrary 1.35x enlargement painted solid gold outside its collider.
  // Fit opaque art to the real circle, and make that contact boundary visible.
  ctx.save(); ctx.strokeStyle = object.kind === 'stone' ? 'rgba(229,231,227,.28)' : 'rgba(255,221,113,.35)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(x / SCALE, y / SCALE, radius, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
  drawImage(ctx, name, x / SCALE - metrics.cx * scale, y / SCALE - metrics.cy * scale, metrics.width * scale, metrics.height * scale);
}
interface CanvasFrame<T extends CanvasGameState> { state: T; pose: ReturnType<typeof hookPose>; frozen: boolean; at: number }
export function GameCanvas<T extends CanvasGameState = CanvasGameState>({ state, reduced = false, frozen = false, onRelease, sample, onFrame, debug = false }: { state: T; reduced?: boolean; frozen?: boolean; onRelease?: () => void; sample?: (now: number) => CanvasFrame<T>; onFrame?: (state: T) => void; debug?: boolean }): JSX.Element {
  const canvas = useRef<HTMLCanvasElement>(null);
  const latest = useRef({ state, frozen, sample, onFrame, debug }); latest.current = { state, frozen, sample, onFrame, debug };
  useEffect(() => {
    for (const name of ['claw_open', 'claw_closed', 'miner_idle', 'miner_pull', 'bg_surface', `bg_underground_${state.dateKey}`, `treasure_${state.dateKey}`, ...Object.values(minerals)]) sprite(name);
  }, [state.dateKey]);
  useEffect(() => {
    let frame = 0, lastState: CanvasGameState | null = null, lastAssets = -1, signature = '', lastPaint = 0, frameCount = 0;
    const element = canvas.current!;
    const layer = document.createElement('canvas'); layer.width = CAMERA.width; layer.height = 1280;
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      // Physics advances at 30Hz, but the sampled hook pose is interpolated
      // between ticks. Draw at up to 60fps so extension and return move in
      // smaller spatial steps. The tolerance handles 60Hz timestamp rounding;
      // 120Hz displays naturally settle near this same 60fps cap.
      if (now - lastPaint < 1000 / 60 - 2) return;
      lastPaint = now;
      frameCount += 1;
      element.dataset.frameCount = String(frameCount);
      const ctx = element.getContext('2d'); if (!ctx) return;
      const live = latest.current.sample?.(now), current = live?.state ?? latest.current.state;
      latest.current.onFrame?.(current);
      element.dataset.frameTime = String(live?.at ?? now);
      element.dataset.engineTick = String(current.tick); element.dataset.enginePhase = current.phase;
      element.dataset.engineObjects = String(current.objects.length); element.dataset.hookStatus = current.hook.status; element.dataset.shots = String(current.shots);
      const staticKey = `${current.dateKey}|${current.objects.map(o => `${o.id}:${o.x}:${o.y}`).join(',')}`;
      const dirtyAssets = currentAssetVersion() !== lastAssets;
      if (signature !== staticKey || dirtyAssets) {
        const base = layer.getContext('2d')!;
        base.clearRect(0, 0, CAMERA.width, 1280); base.fillStyle = '#71503a'; base.fillRect(0, 0, 720, 1280);
        drawImage(base, `bg_underground_${current.dateKey}`, 0, 280, 720, 1000);
        drawImage(base, 'bg_surface', 0, 0, 720, 280);
        for (const object of current.objects) drawMineral(base, object, current.dateKey);
        signature = staticKey; lastAssets = currentAssetVersion();
      }
      if (!reduced || current !== lastState || dirtyAssets || latest.current.debug) {
        const pose = live?.pose ?? hookPose(current, current, 0);
        // One fixed, uniform world-to-screen projection. No following, zooming,
        // or viewport changes at a catch, return, notice, or phase transition.
        ctx.clearRect(0, 0, CAMERA.width, CAMERA.height);
        ctx.save(); ctx.translate(-CAMERA.x, -CAMERA.y); ctx.drawImage(layer, 0, 0);
        const actor = current.hook.status === 'RETRACTING' ? 'miner_pull' : 'miner_idle';
        drawImage(ctx, actor, 196, 52, 190);
        element.dataset.miner = isSpriteDecoded(sprite(actor)) ? actor : 'loading';
        if (current.aimActive) {
          ctx.save(); ctx.setLineDash([12, 14]); ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,239,126,.9)';
          ctx.beginPath(); ctx.moveTo(pose.tip.x / SCALE, pose.tip.y / SCALE); ctx.lineTo(pose.aimEnd.x / SCALE, pose.aimEnd.y / SCALE); ctx.stroke(); ctx.restore();
        }
        const originX = pose.origin.x / SCALE, originY = pose.origin.y / SCALE;
        const ringX = pose.ring.x / SCALE, ringY = pose.ring.y / SCALE;
        ctx.save(); ctx.lineCap = 'round';
        ctx.lineWidth = 9; ctx.strokeStyle = 'rgba(12,8,5,.58)'; ctx.beginPath(); ctx.moveTo(originX, originY); ctx.lineTo(ringX, ringY); ctx.stroke();
        const ropeGradient = ctx.createLinearGradient(originX, originY, ringX, ringY);
        ropeGradient.addColorStop(0, '#6d5b4b'); ropeGradient.addColorStop(.46, '#c8b897'); ropeGradient.addColorStop(.62, '#f1dfaf'); ropeGradient.addColorStop(1, '#8d7963');
        ctx.lineWidth = 5; ctx.strokeStyle = ropeGradient; ctx.beginPath(); ctx.moveTo(originX, originY); ctx.lineTo(ringX, ringY); ctx.stroke();
        ctx.lineWidth = 1.25; ctx.strokeStyle = 'rgba(255,246,210,.78)'; ctx.beginPath(); ctx.moveTo(originX, originY); ctx.lineTo(ringX, ringY); ctx.stroke();
        ctx.restore();
        if (current.hook.carry) drawMineral(ctx, current.hook.carry, current.dateKey, pose.cargo.x, pose.cargo.y);
        const anchor = current.hook.carry ? CLAW_ANCHORS.closed : CLAW_ANCHORS.open;
        ctx.save(); ctx.translate(pose.ring.x / SCALE, pose.ring.y / SCALE); ctx.rotate(pose.rotation);
        drawImage(ctx, current.hook.carry ? 'claw_closed' : 'claw_open', -CLAW_SIZE * anchor.x, -CLAW_SIZE * anchor.y, CLAW_SIZE); ctx.restore();
        if (latest.current.debug) {
          ctx.strokeStyle = '#00e5ff'; ctx.lineWidth = 2;
          for (const o of current.objects) { ctx.beginPath(); ctx.arc(o.x / SCALE, o.y / SCALE, o.radius / SCALE, 0, 2 * Math.PI); ctx.stroke(); }
          ctx.fillStyle = '#ff3355'; ctx.beginPath(); ctx.arc(pose.tip.x / SCALE, pose.tip.y / SCALE, 4, 0, 2 * Math.PI); ctx.fill();
        }
        element.dataset.contactX = String(pose.tip.x / SCALE); element.dataset.contactY = String(pose.tip.y / SCALE);
        ctx.restore();
      }
      lastState = current;
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [reduced]);
  return <div className="game-stage" data-camera="fixed-full-cave"><canvas ref={canvas} width={CAMERA.width} height={CAMERA.height} className="game-canvas" data-testid="game-canvas" data-engine-tick={state.tick} data-engine-phase={state.phase} data-engine-objects={state.objects.length} aria-label={`${t.title}：矿工、完整矿洞与钩爪`} role="img" onClick={onRelease}/></div>;
}
